import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import * as signalR from '@microsoft/signalr';
import { PlaatoMessage, SignalRConnectionState } from '../../models/plaato-message.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PlaatoSignalRService {
  private connection: signalR.HubConnection | null = null;
  private subscribedDevices = new Set<string>();
  private baseApiUrl = `${environment.plaatoApiServer}/api`;

  // Observables for state and data
  private connectionStateSubject = new BehaviorSubject<SignalRConnectionState>(SignalRConnectionState.Disconnected);
  public connectionState$ = this.connectionStateSubject.asObservable();

  private messageUpdateSubject = new Subject<{ deviceId: string, message: PlaatoMessage }>();
  public messageUpdate$ = this.messageUpdateSubject.asObservable();

  private historySubject = new Subject<{ deviceId: string, history: PlaatoMessage[] }>();
  public history$ = this.historySubject.asObservable();

  private deviceListSubject = new Subject<string[]>();
  public deviceList$ = this.deviceListSubject.asObservable();

  private errorSubject = new Subject<string>();
  public error$ = this.errorSubject.asObservable();

  private logSubject = new Subject<{ timestamp: Date, message: string, type: string }>();
  public log$ = this.logSubject.asObservable();

  constructor(private http: HttpClient) { }

  /**
   * Connect to the SignalR hub
   * @param hubUrl The URL of the SignalR hub
   */
  async connect(hubUrl: string): Promise<void> {
    // If already connected, return early
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      this.log('Already connected', 'warning');
      return;
    }

    // If connection exists but is not connected, disconnect first
    if (this.connection && this.connection.state !== signalR.HubConnectionState.Connected) {
      try {
        await this.connection.stop();
        this.log('Stopped previous connection', 'info');
      } catch (err) {
        this.log(`Error stopping previous connection: ${err}`, 'warning');
      }
      this.connection = null;
    }

    this.connectionStateSubject.next(SignalRConnectionState.Connecting);
    this.log('Connecting to SignalR hub...', 'info');

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect()
      .build();

    this.registerEventHandlers();

    try {
      await this.connection.start();
      this.connectionStateSubject.next(SignalRConnectionState.Connected);
      this.log('✅ Connected to SignalR hub', 'success');
    } catch (err) {
      this.connectionStateSubject.next(SignalRConnectionState.Error);
      const errorMessage = `❌ Connection failed: ${err}`;
      this.log(errorMessage, 'error');
      this.errorSubject.next(errorMessage);
      throw err;
    }
  }

  /**
   * Disconnect from the SignalR hub
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connectionStateSubject.next(SignalRConnectionState.Disconnected);
      this.log('🔌 Disconnected from hub', 'info');
      this.subscribedDevices.clear();
    }
  }

  /**
   * Subscribe to updates from a specific device
   * @param deviceId The ID of the device to subscribe to
   */
  async subscribeToDevice(deviceId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      const error = '❌ Not connected to hub';
      this.log(error, 'error');
      throw new Error(error);
    }

    try {
      await this.connection.invoke('SubscribeToDevice', deviceId);
      this.subscribedDevices.add(deviceId);
      this.log(`✅ Subscribed to device: ${deviceId}`, 'success');
    } catch (err) {
      const errorMessage = `❌ Failed to subscribe: ${err}`;
      this.log(errorMessage, 'error');
      this.errorSubject.next(errorMessage);
      throw err;
    }
  }

  /**
   * Unsubscribe from updates from a specific device
   * @param deviceId The ID of the device to unsubscribe from
   */
  async unsubscribeFromDevice(deviceId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      // If not connected, just remove from local tracking
      this.subscribedDevices.delete(deviceId);
      this.log(`Unsubscribed from device: ${deviceId} (not connected)`, 'warning');
      return;
    }

    try {
      await this.connection.invoke('UnsubscribeFromDevice', deviceId);
      this.subscribedDevices.delete(deviceId);
      this.log(`✅ Unsubscribed from device: ${deviceId}`, 'success');
    } catch (err) {
      const errorMessage = `❌ Failed to unsubscribe: ${err}`;
      this.log(errorMessage, 'error');
      this.errorSubject.next(errorMessage);
      throw err;
    }
  }

  /**
   * Get the history of messages for a specific device.
   * Returns a promise that resolves with the history when received from the server.
   * @param deviceId The ID of the device to get history for
   */
  async getHistory(deviceId: string): Promise<PlaatoMessage[]> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      const error = '❌ Not connected to hub';
      this.log(error, 'error');
      throw new Error(error);
    }

    return new Promise<PlaatoMessage[]>((resolve, reject) => {
      // Set up a one-time subscription to the history$ observable
      // We also include a timeout to prevent hanging forever if no response
      const timeoutId = setTimeout(() => {
        subscription.unsubscribe();
        reject(new Error('Timeout waiting for history response'));
      }, 10000); // 10 second timeout

      const subscription = this.history$.subscribe({
        next: ({ deviceId: receivedDeviceId, history }) => {
          if (receivedDeviceId === deviceId) {
            clearTimeout(timeoutId);
            subscription.unsubscribe();
            resolve(history);
          }
        },
        error: (err) => {
          clearTimeout(timeoutId);
          subscription.unsubscribe();
          reject(err);
        }
      });

      // Invoke the hub method
      this.connection?.invoke('GetHistory', deviceId)
        .then(() => {
          this.log(`📡 Requested history for device: ${deviceId}`, 'info');
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          subscription.unsubscribe();
          const errorMessage = `❌ Failed to request history: ${err}`;
          this.log(errorMessage, 'error');
          this.errorSubject.next(errorMessage);
          reject(err);
        });
    });
  }

  /**
   * Get the current temperature for a specific device.
   * Returns a promise that resolves with the temperature when receiving a MessageUpdate from the server.
   * @param deviceId The ID of the device to get temperature for
   */
  async getPlaatoTemperature(deviceId: string): Promise<number | undefined> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      const error = '❌ Not connected to hub';
      this.log(error, 'error');
      throw new Error(error);
    }

    return new Promise<number | undefined>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        subscription.unsubscribe();
        reject(new Error('Timeout waiting for temperature response'));
      }, 10000);

      const subscription = this.messageUpdate$.subscribe({
        next: ({ deviceId: receivedDeviceId, message }) => {
          if (receivedDeviceId === deviceId) {
            clearTimeout(timeoutId);
            subscription.unsubscribe();
            resolve(message.temperature);
          }
        },
        error: (err) => {
          clearTimeout(timeoutId);
          subscription.unsubscribe();
          reject(err);
        }
      });

      // Invoke the hub method - Assuming there's a GetPlaatoTemperature or similar
      this.connection?.invoke('GetPlaatoTemperature', deviceId)
        .then(() => {
          this.log(`📡 Requested temperature for device: ${deviceId}`, 'info');
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          subscription.unsubscribe();
          const errorMessage = `❌ Failed to request temperature: ${err}`;
          this.log(errorMessage, 'error');
          this.errorSubject.next(errorMessage);
          reject(err);
        });
    });
  }

  /**
   * Get the list of available devices
   */
  async getAvailableDevices(): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      const error = '❌ Not connected to hub';
      this.log(error, 'error');
      throw new Error(error);
    }

    try {
      await this.connection.invoke('GetAvailableDevices');
      this.log('📡 Requested device list', 'info');
    } catch (err) {
      const errorMessage = `❌ Failed to get devices: ${err}`;
      this.log(errorMessage, 'error');
      this.errorSubject.next(errorMessage);
      throw err;
    }
  }

  /**
   * Check if currently connected to the hub
   */
  isConnected(): boolean {
    return this.connection !== null && this.connection.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Get the set of currently subscribed device IDs
   */
  getSubscribedDevices(): Set<string> {
    return new Set(this.subscribedDevices);
  }

  /**
   * Set the empty keg weight for a specific device
   * @param deviceId The ID of the device
   * @param weight The empty keg weight in grams
   * @returns Observable with the API response
   */
  setKegWeight(deviceId: string, weight: number): Observable<{ success: boolean, deviceId: string, weight: number, message: string }> {
    const url = `${this.baseApiUrl}/keg/${deviceId}/weight/${weight}`;
    this.log(`📡 Setting keg weight for ${deviceId}: ${weight}g`, 'info');
    return this.http.post<{ success: boolean, deviceId: string, weight: number, message: string }>(url, null);
  }

  /**
   * Set the empty keg weight for a specific device
   * @param deviceId The ID of the device
   * @param weight The empty keg weight in grams
   * @returns Observable with the API response
   */
  setSensitivity(deviceId: string, sensitivity: number): Observable<{ success: boolean, deviceId: string, sensitivity: number, message: string }> {
    const url = `${this.baseApiUrl}/sensitivity/${deviceId}/value/${sensitivity}`;
    this.log(`📡 Setting sensitivity for ${deviceId}: ${sensitivity}`, 'info');
    return this.http.post<{ success: boolean, deviceId: string, sensitivity: number, message: string }>(url, null);
  }

  /**
   * Set the temperature offset for a specific device
   * @param deviceId The ID of the device
   * @param offset The temperature offset in degrees Celsius
   * @returns Observable with the API response
   */
  setTemperatureOffset(deviceId: string, offset: number): Observable<{ success: boolean, deviceId: string, offset: number, message: string }> {
    const url = `${this.baseApiUrl}/temperature-offset/${deviceId}/value/${offset}`;
    this.log(`📡 Setting temp offset for ${deviceId}: ${offset}`, 'info');
    return this.http.post<{ success: boolean, deviceId: string, offset: number, message: string }>(url, null);
  }

  /**
   * Set the content mode (beer/CO2) for a specific device — hardware flag `vw 88`.
   * @param deviceId The ID of the device
   * @param mode 1 = beer, 2 = CO2
   * @returns Observable with the API response. `queued: true` means the device
   *          was offline and the command is stored until it reconnects.
   */
  setKegContentMode(deviceId: string, mode: 1 | 2): Observable<{ success: boolean, deviceId: string, mode: number, message: string, queued: boolean }> {
    const url = `${this.baseApiUrl}/keg/${deviceId}/content-mode/${mode}`;
    this.log(`📡 Setting content mode for ${deviceId}: ${mode === 1 ? 'beer' : 'co2'}`, 'info');
    return this.http.post<{ success: boolean, deviceId: string, mode: number, message: string, queued: boolean }>(url, null);
  }

  /**
   * Register all SignalR event handlers
   */
  private registerEventHandlers(): void {
    if (!this.connection) return;

    // MessageUpdate event
    this.connection.on('MessageUpdate', (deviceId: string, message: PlaatoMessage) => {
      this.messageUpdateSubject.next({ deviceId, message });
    });

    // ReceiveHistory event
    this.connection.on('ReceiveHistory', (deviceId: string, history: PlaatoMessage[]) => {

      const lastFiftyMessages = history.slice(-50);
      this.log(`📜 Received history for ${deviceId} (${history.length} messages)`, 'info');
      this.historySubject.next({ deviceId, history: lastFiftyMessages });
      //this.historySubject.next({ deviceId, lastFiftyMessages });
    });

    // ReceiveDeviceList event
    this.connection.on('ReceiveDeviceList', (devices: string[]) => {
      this.log(`📱 Received device list: ${devices.join(', ')}`, 'info');
      this.deviceListSubject.next(devices);
    });

    // Error event
    this.connection.on('Error', (errorMessage: string) => {
      this.log(`❌ Error: ${errorMessage}`, 'error');
      this.errorSubject.next(errorMessage);
    });

    // Reconnecting event
    this.connection.onreconnecting(() => {
      this.connectionStateSubject.next(SignalRConnectionState.Reconnecting);
      this.log('⚠️ Reconnecting...', 'warning');
    });

    // Reconnected event
    this.connection.onreconnected(() => {
      this.connectionStateSubject.next(SignalRConnectionState.Connected);
      this.log('✅ Reconnected', 'success');
    });

    // Connection closed event
    this.connection.onclose(() => {
      this.connectionStateSubject.next(SignalRConnectionState.Disconnected);
      this.log('🔌 Connection closed', 'info');
    });
  }

  /**
   * Internal logging method that emits to the log$ observable
   */
  private log(message: string, type: string): void {
    this.logSubject.next({
      timestamp: new Date(),
      message,
      type
    });
  }
}
